import React, { useCallback, useEffect, useState } from 'react';

import { IAppliance } from '../../interfaces';
import arrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Arrow-Icon.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export default function ListingFilter(props: {
    filteredProducts: IAppliance[];
    setFilteredProducts: React.Dispatch<IAppliance[]>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    priceAndCode: any[];
    setSort: (value: string) => void;
    sort: string;
    showSmallFilter: boolean;
    setShowSmallFilter?: React.Dispatch<React.SetStateAction<boolean>>;
    setDisplayMobileFilter?: React.Dispatch<React.SetStateAction<boolean>>;
    displayMobileFilter?: boolean;
    isParts?: boolean;
}) {
    const {
        filteredProducts,
        setFilteredProducts,
        setPage,
        setSort,
        sort,
        showSmallFilter,
        setShowSmallFilter,
        setDisplayMobileFilter,
        displayMobileFilter,
        isParts = false,
    } = props;
    const [showFilter, setShowFilter] = useState(false);
    const { t } = useTranslation('layout');
    const [currentFilterType, setCurrentFilterType]: [
        string,
        React.Dispatch<React.SetStateAction<string>>,
    ] = useState(t('appliance.filter.size'));
    const { currentLocale } = usePageLocalization();
    const cleanUp = useCallback(
        (filterTitle: string) => {
            setCurrentFilterType(filterTitle);
            setShowFilter(false);
            setPage(1);
        },
        [setPage]
    );
    const updateSort = useCallback(() => {
        let sortedProd = [...filteredProducts];
        switch (sort) {
            case 'name':
                sortedProd = sortedProd.sort((prodA, prodB) => {
                    return prodA?.title[currentLocale]?.localeCompare(
                        prodB?.title[currentLocale]
                    );
                });
                break;
            case 'date':
                sortedProd = sortedProd.sort((prodA: any, prodB: any) => {
                    const dateA = prodA?.attributes?.dateAdded?.values?.[0]
                        ? new Date(
                              prodA?.attributes?.dateAdded?.values?.[0]?.[
                                  currentLocale
                              ]
                          ).valueOf()
                        : 0;
                    const dateB = prodB?.attributes?.dateAdded?.values?.[0]
                        ? new Date(
                              prodB?.attributes?.dateAdded?.values?.[0]?.[
                                  currentLocale
                              ]
                          ).valueOf()
                        : 0;
                    return dateA - dateB;
                });
                break;
            case 'price-low':
                sortedProd = sortedProd.sort((a: any, b: any) => {
                    if (a.price?.finalPrice?.value < b.price?.finalPrice?.value)
                        return -1;
                    if (a.price?.finalPrice?.value > b.price?.finalPrice?.value)
                        return 1;
                    return 0;
                });
                break;
            case 'price-high':
                sortedProd = sortedProd.sort((a: any, b: any) => {
                    if (a.price?.finalPrice?.value > b.price?.finalPrice?.value)
                        return -1;
                    if (a.price?.finalPrice?.value < b.price?.finalPrice?.value)
                        return 1;
                    return 0;
                });
                break;
            default:
                sortedProd = sortedProd.sort((prodA: any, prodB: any) => {
                    return prodB?.attributes?.size?.values?.[0]?.[
                        'en_CA'
                    ].localeCompare(
                        prodA?.attributes?.size?.values?.[0]?.['en_CA']
                    );
                });
                break;
        }
        setFilteredProducts(sortedProd);
    }, [currentLocale, filteredProducts, setFilteredProducts, sort]);
    useEffect(() => {
        if (sort) {
            updateSort();
        } else {
            setCurrentFilterType(currentLocale === 'fr_CA' ? 'Taille' : 'Size');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocale, sort]);

    useEffect(() => {
        if (sort === '') {
            cleanUp(t(`appliance.filter.size`));
        } else {
            cleanUp(t(`appliance.filter.${sort}`));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    const normal = 'second-filter-elem';
    const selected = 'second-filter-elem filter-selected';
    const getClassName = (val: string) => {
        if (val === currentFilterType) return selected;
        return normal;
    };
    return (
        <>
            <div className='appliance-second-filter-container'>
                {!isParts && (
                    <div
                        className='small-filter-btn'
                        onClick={() => {
                            if (setShowSmallFilter)
                                setShowSmallFilter(!showSmallFilter);
                            if (setDisplayMobileFilter)
                                setDisplayMobileFilter(!displayMobileFilter);
                        }}
                    >
                        <span>{t('venting.filter')}</span>
                        <img
                            className={`iconRight`}
                            src={arrowIcon}
                            alt=''
                            style={
                                showSmallFilter
                                    ? { transform: 'rotate(-90deg)' }
                                    : { transform: 'rotate(90deg)' }
                            }
                        />
                    </div>
                )}
                <div
                    className='appliance-second-filter'
                    onClick={() => setShowFilter(!showFilter)}
                >
                    <div className='second-filter-title'>
                        <span>{t('appliance.filter.sort-by')}</span>
                        <img
                            className={`iconRight`}
                            src={arrowIcon}
                            alt=''
                            style={
                                showFilter
                                    ? { transform: 'rotate(-90deg)' }
                                    : { transform: 'rotate(90deg)' }
                            }
                        />
                    </div>
                    <div
                        className='appliance-filter-list'
                        style={{ display: showFilter ? 'block' : 'none' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className={getClassName(t('appliance.filter.name'))}
                            onClick={() => {
                                setSort('name');
                            }}
                        >
                            <span>{t('appliance.filter.name')}</span>
                        </div>
                        <div
                            className={getClassName(t('appliance.filter.size'))}
                            onClick={() => setSort('size')}
                        >
                            <span>{t('appliance.filter.size')}</span>
                        </div>
                        <div
                            className={getClassName(t('appliance.filter.date'))}
                            onClick={() => setSort('date')}
                        >
                            <span>{t('appliance.filter.date')}</span>
                        </div>
                        <div
                            className={getClassName(
                                t('appliance.filter.price-low')
                            )}
                            onClick={() => setSort('price-low')}
                        >
                            <span>{t('appliance.filter.price-low')}</span>
                        </div>
                        <div
                            className={getClassName(
                                t('appliance.filter.price-high')
                            )}
                            onClick={() => setSort('price-high')}
                        >
                            <span>{t('appliance.filter.price-high')}</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
